import { API_BASE } from '../../lib/apiOrigin';
import { useAdminLanguage } from "../../i18n/adminTranslations";
import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  RefreshCw, Plus, Trash2, Edit3, Eye, EyeOff, X, Save, Star, ShieldCheck, Upload, ExternalLink,
} from "lucide-react";

const API = API_BASE;

const emptyDraft = () => ({
  customer_name: "",
  rating: 5,
  text: "",
  review_date: "",
  profile_photo: "",
  google_review_url: "",
  verified: false,
  display_order: 0,
  is_active: true,
});

const GoogleReviewsTab = ({ token }) => {
  const { a } = useAdminLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(emptyDraft());
  const [saving, setSaving] = useState(false);
  const [businessUrl, setBusinessUrl] = useState("");
  const [savingBiz, setSavingBiz] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [r, b] = await Promise.all([
        axios.get(`${API}/admin/google-reviews`, { headers: { "X-Admin-Token": token } }),
        axios.get(`${API}/settings/business-profile`).catch(() => ({ data: {} })),
      ]);
      setItems(Array.isArray(r.data) ? r.data : []);
      setBusinessUrl(b.data?.google_business_url || "");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load reviews.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setDraft({ ...emptyDraft(), display_order: items.length });
    setEditingId("new");
  };
  const startEdit = (item) => {
    setDraft({ ...emptyDraft(), ...item });
    setEditingId(item.id);
  };
  const cancelEdit = () => {
    setDraft(emptyDraft());
    setEditingId(null);
  };

  const save = async () => {
    if (!draft.customer_name.trim() || !draft.text.trim()) {
      setError("Customer name and review text are required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (editingId === "new") {
        const res = await axios.post(`${API}/admin/google-reviews`, draft, {
          headers: { "X-Admin-Token": token },
        });
        setItems((prev) => [...prev, res.data]);
      } else {
        const res = await axios.put(`${API}/admin/google-reviews/${editingId}`, draft, {
          headers: { "X-Admin-Token": token },
        });
        setItems((prev) => prev.map((i) => (i.id === editingId ? res.data : i)));
      }
      cancelEdit();
    } catch (e) {
      setError(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try {
      await axios.delete(`${API}/admin/google-reviews/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (e) {
      setError("Delete failed.");
    }
  };

  const toggleActive = async (item) => {
    try {
      const res = await axios.put(
        `${API}/admin/google-reviews/${item.id}`,
        { is_active: !item.is_active },
        { headers: { "X-Admin-Token": token } }
      );
      setItems((prev) => prev.map((i) => (i.id === item.id ? res.data : i)));
    } catch (e) {
      setError("Failed to toggle visibility.");
    }
  };

  const move = async (item, dir) => {
    const idx = items.findIndex((i) => i.id === item.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const other = items[swap];
    const optimistic = [...items];
    optimistic[idx] = { ...other, display_order: item.display_order };
    optimistic[swap] = { ...item, display_order: other.display_order };
    setItems(optimistic);
    try {
      await Promise.all([
        axios.put(`${API}/admin/google-reviews/${item.id}`, { display_order: other.display_order }, { headers: { "X-Admin-Token": token } }),
        axios.put(`${API}/admin/google-reviews/${other.id}`, { display_order: item.display_order }, { headers: { "X-Admin-Token": token } }),
      ]);
    } catch (e) {
      setError("Reorder failed. Refreshing.");
      load();
    }
  };

  const saveBusinessUrl = async () => {
    setSavingBiz(true);
    setError("");
    try {
      await axios.put(
        `${API}/admin/settings/business-profile`,
        { google_business_url: businessUrl },
        { headers: { "X-Admin-Token": token } }
      );
    } catch (e) {
      setError("Failed to save Google Business URL.");
    } finally {
      setSavingBiz(false);
    }
  };

  return (
    <div data-testid="google-reviews-tab">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <h2 className="font-display font-black text-white text-3xl sm:text-4xl mb-2">{a("Google Reviews")}</h2>
          <p className="text-zinc-300 text-base max-w-2xl leading-relaxed">
            Real reviews you've copied from Google Business Profile. They rotate in the public homepage carousel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="reviews-new"
            onClick={startNew}
            className="inline-flex items-center gap-2 border border-sky-400 text-sky-300 hover:bg-sky-500/10 px-5 py-3 text-base font-bold uppercase tracking-[0.12em]"
          >
            <Plus size={18} /> {a("New")}
          </button>
          <button
            data-testid="reviews-refresh"
            onClick={load}
            className="h-11 w-11 border border-white/15 text-white hover:border-sky-400 flex items-center justify-center"
          >
            <RefreshCw size={18} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {/* Global "Read More on Google" URL */}
      <div className="mb-8 border border-white/10 bg-zinc-950/60 p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <ExternalLink size={16} className="text-sky-400" />
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-300">Google Business Profile URL</span>
        </div>
        <p className="text-zinc-400 text-sm mb-3 leading-relaxed">
          Shown as the "Read More Reviews on Google" button on the homepage. Leave empty to hide the button.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            data-testid="reviews-business-url"
            value={businessUrl}
            onChange={(e) => setBusinessUrl(e.target.value)}
            className="flex-1 bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
            placeholder="https://g.page/r/…"
            maxLength={600}
          />
          <button
            data-testid="reviews-save-business-url"
            onClick={saveBusinessUrl}
            disabled={savingBiz}
            className="btn-primary inline-flex items-center gap-2 justify-center disabled:opacity-50"
          >
            <Save size={16} />
            {a(savingBiz ? "Saving…" : "Save")}
          </button>
        </div>
      </div>

      {error && (
        <div data-testid="reviews-error" className="text-red-300 text-base mb-5 bg-red-500/10 border border-red-500/30 px-4 py-3">
          {a(error)}
        </div>
      )}

      {editingId && (
        <ReviewForm
          draft={draft}
          setDraft={setDraft}
          onCancel={cancelEdit}
          onSave={save}
          saving={saving}
          isNew={editingId === "new"}
        />
      )}

      <div className="space-y-4 mt-6">
        {items.length === 0 && !loading ? (
          <div className="border border-white/10 p-10 text-center text-zinc-300">
            No reviews yet. Click <span className="text-sky-300 font-semibold">+ New</span> to add one.
          </div>
        ) : (
          items.map((item, idx) => (
            <ReviewCard
              key={item.id}
              item={item}
              onEdit={() => startEdit(item)}
              onDelete={() => remove(item.id)}
              onToggle={() => toggleActive(item)}
              onMoveUp={() => move(item, -1)}
              onMoveDown={() => move(item, +1)}
              canMoveUp={idx > 0}
              canMoveDown={idx < items.length - 1}
            />
          ))
        )}
      </div>
    </div>
  );
};

const StarRow = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={14} className={i <= rating ? "text-amber-400 fill-amber-400" : "text-zinc-600"} />
    ))}
  </div>
);

const ReviewCard = ({ item, onEdit, onDelete, onToggle, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) => {
  const { a } = useAdminLanguage();
  return (<div
    data-testid={`review-card-${item.id}`}
    className={`border p-5 sm:p-6 flex flex-col sm:flex-row gap-5 items-start ${
      item.is_active ? "border-white/15 bg-zinc-950/60" : "border-white/5 bg-zinc-950/30 opacity-60"
    }`}
  >
    <div className="flex flex-col gap-1 pt-1">
      <button
        onClick={onMoveUp}
        disabled={!canMoveUp}
        className="h-7 w-7 border border-white/10 text-zinc-400 hover:text-sky-400 hover:border-sky-400 disabled:opacity-30 flex items-center justify-center"
      >
        ↑
      </button>
      <button
        onClick={onMoveDown}
        disabled={!canMoveDown}
        className="h-7 w-7 border border-white/10 text-zinc-400 hover:text-sky-400 hover:border-sky-400 disabled:opacity-30 flex items-center justify-center"
      >
        ↓
      </button>
    </div>

    <div className="shrink-0">
      {item.profile_photo ? (
        <img src={item.profile_photo} alt="" className="h-14 w-14 rounded-full object-cover border border-white/10" />
      ) : (
        <div className="h-14 w-14 rounded-full bg-sky-500/20 border border-sky-400/40 flex items-center justify-center text-sky-300 font-display font-black text-xl">
          {(item.customer_name || "?")[0]?.toUpperCase()}
        </div>
      )}
    </div>

    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="text-white font-semibold text-base">{item.customer_name}</span>
        {item.verified && (
          <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-400">
            <ShieldCheck size={12} /> Verified
          </span>
        )}
        {!item.is_active && (
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-400">· Hidden</span>
        )}
      </div>
      <div className="flex items-center gap-3 mb-2">
        <StarRow rating={item.rating} />
        {item.review_date && <span className="text-zinc-500 text-xs">{item.review_date}</span>}
      </div>
      <p className="text-zinc-300 text-sm leading-relaxed line-clamp-3 whitespace-pre-line">{item.text}</p>
    </div>

    <div className="flex sm:flex-col gap-2 shrink-0">
      <button
        data-testid={`review-edit-${item.id}`}
        onClick={onEdit}
        className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-sky-400 hover:text-sky-300 px-4 py-2 text-sm font-semibold"
      >
        <Edit3 size={14} /> {a("Edit")}
      </button>
      <button
        data-testid={`review-toggle-${item.id}`}
        onClick={onToggle}
        className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-amber-400 hover:text-amber-300 px-4 py-2 text-sm font-semibold"
      >
        {item.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
      <button
        data-testid={`review-delete-${item.id}`}
        onClick={onDelete}
        className="inline-flex items-center gap-1.5 border border-white/15 text-white hover:border-red-400 hover:text-red-300 px-4 py-2 text-sm font-semibold"
      >
        <Trash2 size={14} />
      </button>
    </div>
  </div>);
};

const ReviewForm = ({ draft, setDraft, onCancel, onSave, saving, isNew }) => {
  const { a } = useAdminLanguage();
  const fileRef = useRef(null);
  const handleFile = (file) => {
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      setDraft((d) => ({ ...d, _error: "Photo too large (max 2MB)." }));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setDraft((d) => ({ ...d, profile_photo: reader.result }));
    reader.readAsDataURL(file);
  };

  return (
    <div data-testid="review-form" className="border border-sky-400/50 bg-zinc-950 p-6 sm:p-8 space-y-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-display font-black text-white text-2xl">
          {isNew ? "New Google review" : "Edit Google review"}
        </h3>
        <button onClick={onCancel} className="text-zinc-400 hover:text-white p-2" title={a("Close")}>
          <X size={20} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Customer name *</span>
          <input
            data-testid="review-input-name"
            value={draft.customer_name}
            onChange={(e) => setDraft((d) => ({ ...d, customer_name: e.target.value }))}
            className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
            placeholder="e.g. Sarah M."
            maxLength={180}
          />
        </label>
        <label className="block">
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Review date</span>
          <input
            data-testid="review-input-date"
            value={draft.review_date || ""}
            onChange={(e) => setDraft((d) => ({ ...d, review_date: e.target.value }))}
            className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
            placeholder="e.g. 3 weeks ago"
            maxLength={40}
          />
        </label>
      </div>

      <div>
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Rating *</span>
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((r) => (
            <button
              key={r}
              type="button"
              data-testid={`review-rating-${r}`}
              onClick={() => setDraft((d) => ({ ...d, rating: r }))}
              className="p-1"
              aria-label={`${r} stars`}
            >
              <Star size={30} className={r <= draft.rating ? "text-amber-400 fill-amber-400" : "text-zinc-600"} />
            </button>
          ))}
        </div>
      </div>

      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Review text *</span>
        <textarea
          data-testid="review-input-text"
          value={draft.text}
          onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
          rows={5}
          className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none resize-none"
          placeholder="Paste the customer's real Google review — do not paraphrase."
          maxLength={4000}
        />
      </label>

      <label className="block">
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Google review URL (optional)</span>
        <input
          data-testid="review-input-url"
          value={draft.google_review_url || ""}
          onChange={(e) => setDraft((d) => ({ ...d, google_review_url: e.target.value }))}
          className="w-full bg-zinc-900 border border-white/15 focus:border-sky-400 text-white px-4 py-3 outline-none"
          placeholder="https://g.co/…"
          maxLength={600}
        />
      </label>

      <div>
        <span className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-300 mb-2 block">Customer photo (optional)</span>
        <div className="flex items-start gap-4">
          {draft.profile_photo ? (
            <div className="relative">
              <img src={draft.profile_photo} alt="" className="h-20 w-20 rounded-full object-cover border border-white/10" />
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, profile_photo: "" }))}
                className="absolute -top-1 -right-1 h-6 w-6 bg-black border border-red-400 text-red-300 flex items-center justify-center rounded-full"
              >
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="h-20 w-20 rounded-full border border-dashed border-white/15 flex items-center justify-center text-zinc-600 text-xs">
              No photo
            </div>
          )}
          <div className="flex flex-col gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="review-input-photo"
            />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 border border-white/20 text-white hover:border-sky-400 px-4 py-2.5 text-sm font-semibold"
            >
              <Upload size={14} /> {a("Upload photo")}
            </button>
            <p className="text-zinc-500 text-xs">Max 2MB. Round crop preferred.</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="inline-flex items-center gap-3 select-none">
          <input
            type="checkbox"
            data-testid="review-input-verified"
            checked={!!draft.verified}
            onChange={(e) => setDraft((d) => ({ ...d, verified: e.target.checked }))}
            className="h-5 w-5 accent-emerald-500"
          />
          <span className="text-zinc-200 text-sm inline-flex items-center gap-1">
            <ShieldCheck size={14} className="text-emerald-400" /> Verified badge
          </span>
        </label>
        <label className="inline-flex items-center gap-3 select-none">
          <input
            type="checkbox"
            data-testid="review-input-active"
            checked={!!draft.is_active}
            onChange={(e) => setDraft((d) => ({ ...d, is_active: e.target.checked }))}
            className="h-5 w-5 accent-sky-500"
          />
          <span className="text-zinc-200 text-sm">{a("Show publicly on the site")}</span>
        </label>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/10">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          className="border border-white/15 text-zinc-200 hover:border-white/30 px-5 py-3 text-sm font-bold uppercase tracking-[0.12em]"
        >
          {a("Cancel")}
        </button>
        <button
          type="button"
          data-testid="review-form-save"
          onClick={onSave}
          disabled={saving}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Save size={16} />
          {a(saving ? "Saving…" : "Save")}
        </button>
      </div>
    </div>
  );
};

export default GoogleReviewsTab;
